import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { getAboutImages, uploadAboutImages, setCurrentAboutImage, deleteAboutImage } from '../services/api';

// Helper function to get display names for sections
const getSectionDisplayName = (section) => {
  switch (section) {
    case 'intro':
      return 'O nás sekcia';
    case 'hero':
      return 'Hero sekcia (About Us)';
    case 'hero-about-left':
      return 'O nás stránka - Ľavá strana';
    case 'hero-about-right':
      return 'O nás stránka - Pravá strana';
    case 'hero-left':
      return 'Hlavná stránka - Ľavá strana';
    case 'hero-right':
      return 'Hlavná stránka - Pravá strana';
    case 'hero-top-left':
      return 'Hlavná stránka - Ľavý horný';
    case 'hero-top-right':
      return 'Hlavná stránka - Pravý horný';
    case 'hero-bottom-left':
      return 'Hlavná stránka - Ľavý dolný';
    case 'hero-bottom-right':
      return 'Hlavná stránka - Pravý dolný';
    case 'background':
      return 'Pozadie sekcia';
    default:
      return section;
  }
};

function AboutImageSlideshow({ 
  section, 
  isAdmin, 
  customImageStyle = {}, 
  customContainerStyle = {} 
}) {
  const [aboutImages, setAboutImages] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // Debug logging
  console.log(`AboutImageSlideshow - Section: ${section}, IsAdmin: ${isAdmin}`);

  // Load images on component mount
  const loadImages = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await getAboutImages(section);
      setAboutImages(data);
      setCurrentImageIndex(data.currentImageIndex || 0);
    } catch (error) {
      console.error('Error loading about images:', error);
      
      // Create empty data structure when API fails - no fallback images
      const emptyData = {
        name: section,
        images: [],
        currentImageIndex: 0
      };
      
      setAboutImages(emptyData);
      setCurrentImageIndex(0);
    } finally {
      setIsLoading(false);
    }
  }, [section]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  // Auto-advance slideshow
  useEffect(() => {
    if (!aboutImages?.images || aboutImages.images.length <= 1 || isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex(prevIndex => {
        const nextIndex = (prevIndex + 1) % aboutImages.images.length;
        return nextIndex;
      });
    }, 4000); // Change image every 4 seconds
    
    return () => clearInterval(interval);
  }, [aboutImages?.images, isPaused]);

  // Pause slideshow on hover when admin
  useEffect(() => {
    if (isAdmin && isHovered) {
      setIsPaused(true);
    } else {
      setIsPaused(false);
    }
  }, [isAdmin, isHovered]);

  const handleImageClick = () => {
    if (isAdmin) {
      setShowManageModal(true);
    }
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    // Check if adding these files would exceed 6 images total
    const currentImageCount = aboutImages?.images?.length || 0;
    const availableSlots = 6 - currentImageCount;
    
    if (files.length > availableSlots) {
      alert(`Môžete pridať iba ${availableSlots} ďalších obrázkov (maximálne 6 celkom)`);
      return;
    }
    
    try {
      setIsUploading(true);
      const result = await uploadAboutImages(section, Array.from(files));
      setAboutImages(result.aboutImages);
      console.log('Images uploaded successfully');
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Nahrávanie obrázkov zatiaľ nie je dostupné. Server potrebuje byť aktualizovaný novými About Us API endpointmi. Prosím kontaktujte vývojára.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSetCurrentImage = async (imageIndex) => {
    // Update local state immediately for smooth UI
    setCurrentImageIndex(imageIndex);
    
    try {
      const result = await setCurrentAboutImage(section, imageIndex);
      setAboutImages(result);
    } catch (error) {
      console.error('Error setting current image:', error);
      alert('Správa obrázkov zatiaľ nie je dostupná. Server potrebuje byť aktualizovaný novými About Us API endpointmi.');
      // Revert local state if API call fails
      setCurrentImageIndex(aboutImages?.currentImageIndex || 0);
    }
  };

  const handleDeleteImage = async (imageIndex) => {
    if (!window.confirm('Naozaj chcete vymazať tento obrázok?')) return;
    
    try {
      const result = await deleteAboutImage(section, imageIndex);
      setAboutImages(result);
      setCurrentImageIndex(result.currentImageIndex);
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Vymazávanie obrázkov zatiaľ nie je dostupné. Server potrebuje byť aktualizovaný novými About Us API endpointmi.');
    }
  };

  const getCurrentImage = () => {
    if (!aboutImages?.images?.length) return null;
    return aboutImages.images[currentImageIndex] || aboutImages.images[0];
  };

  if (isLoading) {
    return (
      <div style={{
        ...styles.imageContainer,
        ...customContainerStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={styles.loadingSpinner}></div>
      </div>
    );
  }

  // Always render the container, even if no images
  const hasImages = aboutImages?.images?.length > 0;

  return (
    <>
      <div 
        style={{
          ...styles.imageContainer,
          ...customContainerStyle
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleImageClick}
      >
        {hasImages ? (
          // Show images if available
          <>
            {/* Image slideshow with fade transitions */}
            {aboutImages.images.map((image, index) => (
              <motion.img
                key={index}
                style={{
                  ...styles.image,
                  ...customImageStyle,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
                src={image.fileUrl || image.relativePath}
                alt={`${getSectionDisplayName(section)} - obrázok ${index + 1}`}
                initial={{ opacity: 0 }}
                animate={{ 
                  opacity: index === currentImageIndex ? 1 : 0,
                  zIndex: index === currentImageIndex ? 1 : 0
                }}
                transition={{ 
                  duration: 0.8,
                  ease: "easeInOut"
                }}
              />
            ))}
            
            {/* Slideshow indicators */}
            {aboutImages.images.length > 1 && (
              <div style={styles.indicators}>
                {aboutImages.images.map((_, index) => (
                  <div
                    key={index}
                    style={{
                      ...styles.indicator,
                      backgroundColor: index === currentImageIndex ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'
                    }}
                    onClick={() => setCurrentImageIndex(index)}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          // Show placeholder text when no images
          <div style={styles.placeholderContainer}>
            <div style={styles.placeholderText}>
              EUROCENTRUM PRIEVIDZA
            </div>
          </div>
        )}
        
        {/* Admin overlay - always visible for admin users */}
        {isAdmin && (
          <motion.div
            style={styles.adminOverlayBottomRight}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div style={styles.editIcon}>📸</div>
          </motion.div>
        )}
      </div>

      {/* Image Management Modal */}
      {showManageModal && isAdmin && (
        <div style={styles.modalOverlay} onClick={() => setShowManageModal(false)}>
          <motion.div
            style={styles.modal}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Spravovať {getSectionDisplayName(section)} obrázky</h3>
              <button
                style={styles.closeButton}
                onClick={() => setShowManageModal(false)}
              >
                ×
              </button>
            </div>

            <div style={styles.modalContent}>
              {/* Upload Section */}
              <div style={styles.uploadSection}>
                <label style={styles.uploadButton}>
                  {isUploading ? 'Nahrávam...' : '+ Pridať obrázky'}
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileUpload(e.target.files)}
                    disabled={isUploading}
                  />
                </label>
                <p style={styles.uploadHint}>
                  Nahrajte až {6 - (aboutImages?.images?.length || 0)} ďalších obrázkov (maximálne 6 celkom)
                </p>
              </div>

              {/* Images Grid - only show if there are images */}
              {hasImages && (
                <div style={styles.imagesGrid}>
                  {aboutImages.images.map((image, index) => (
                    <div key={index} style={styles.imageItem}>
                      <img
                        style={{
                          ...styles.thumbnailImage,
                          border: index === currentImageIndex ? '3px solid #2c69a0' : '2px solid #ddd'
                        }}
                        src={image.fileUrl || image.relativePath}
                        alt={`${getSectionDisplayName(section)} - obrázok ${index + 1}`}
                        onClick={() => handleSetCurrentImage(index)}
                      />
                      <div style={styles.imageActions}>
                        <button
                          style={styles.setCurrentButton}
                          onClick={() => handleSetCurrentImage(index)}
                          disabled={index === currentImageIndex}
                        >
                          {index === currentImageIndex ? 'Aktuálny' : 'Nastaviť ako aktuálny'}
                        </button>
                        <button
                          style={styles.deleteButton}
                          onClick={() => handleDeleteImage(index)}
                        >
                          Vymazať
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Show message when no images */}
              {!hasImages && (
                <div style={styles.noImagesMessage}>
                  <p style={styles.noImagesText}>
                    Zatiaľ nie sú nahrané žiadne obrázky pre túto sekciu.
                  </p>
                  <p style={styles.noImagesSubtext}>
                    Kliknite na tlačidlo vyššie pre pridanie obrázkov.
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}

const styles = {
  imageContainer: {
    position: 'relative',
    cursor: 'pointer',
    overflow: 'hidden'
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s ease'
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #2c69a0',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: '1px solid #eee'
  },
  modalTitle: {
    margin: 0,
    fontSize: '1.5rem',
    color: '#333'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: '#666',
    padding: 0,
    lineHeight: 1
  },
  modalContent: {
    padding: '1.5rem'
  },
  uploadSection: {
    marginBottom: '2rem',
    textAlign: 'center'
  },
  uploadButton: {
    display: 'inline-block',
    backgroundColor: '#2c69a0',
    color: 'white',
    padding: '0.75rem 1.5rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 'bold',
    border: 'none'
  },
  uploadHint: {
    margin: '0.5rem 0 0 0',
    color: '#666',
    fontSize: '0.9rem'
  },
  imagesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '1rem'
  },
  imageItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  thumbnailImage: {
    width: '100%',
    height: '150px',
    objectFit: 'cover',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease'
  },
  imageActions: {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '0.5rem'
  },
  setCurrentButton: {
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    padding: '0.4rem 0.8rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem'
  },
  deleteButton: {
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    padding: '0.4rem 0.8rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem'
  },
  adminOverlayBottomRight: {
    position: 'absolute',
    bottom: '50px',
    right: '10px',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    borderRadius: '12px',
    padding: '0.5rem',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    border: '2px solid #28a745',
    zIndex: 10
  },
  editIcon: {
    fontSize: '1.5rem',
    marginBottom: 0
  },
  indicators: {
    position: 'absolute',
    bottom: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '0.5rem'
  },
  indicator: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  placeholderContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    borderRadius: '8px',
    backgroundColor: '#f0f0f0',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s ease'
  },
  placeholderText: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.1)',
    '@media (max-width: 768px)': {
      fontSize: '1.2rem'
    }
  },
  noImagesMessage: {
    textAlign: 'center',
    padding: '1rem',
    color: '#666'
  },
  noImagesText: {
    margin: 0
  },
  noImagesSubtext: {
    margin: '0.5rem 0 0 0'
  }
};

export default AboutImageSlideshow; 